import React, { useState } from "react";

const sections = [
  {
    title: "1. Information We Collect",
    intro: "We may collect the following information:",
    bullets: [
      "Full Name",
      "Email Address",
      "Phone Number",
      "Delivery Address",
      "Payment Information",
      "Order History",
      "Device and browser information",
    ],
  },
  {
    title: "2. How We Use Your Information",
    intro: "Your information is used to:",
    bullets: [
      "Process and deliver orders",
      "Verify payments",
      "Provide customer support",
      "Improve our services",
      "Send order updates and confirmations",
      "Prevent fraud and unauthorized activities",
    ],
  },
  {
    title: "3. Payment Security",
    intro: "All payments are securely processed through Flutterwave. We do not directly store:",
    bullets: [
      "Debit card details",
      "Banking PINs",
      "Sensitive financial credentials",
    ],
    footer:
      "Payment processing is protected using industry-standard encryption and fraud prevention systems.",
  },
  {
    title: "4. Data Protection",
    intro:
      "We implement appropriate security measures to protect customer information against:",
    bullets: [
      "Unauthorized access",
      "Loss",
      "Misuse",
      "Alteration",
      "Disclosure",
    ],
    footer:
      "However, while we strive for maximum security, no online platform can guarantee 100% absolute protection.",
  },
  {
    title: "5. Sharing of Information",
    content: "We do not sell or rent customer information to third parties.",
    intro2: "Information may only be shared with:",
    bullets: [
      "Payment providers",
      "Delivery/logistics partners",
      "Legal authorities when required by law",
    ],
  },
  {
    title: "6. Cookies & Website Analytics",
    intro: "Our website may use cookies and analytics tools to:",
    bullets: [
      "Improve website performance",
      "Understand customer behavior",
      "Enhance user experience",
    ],
    footer: "Customers may disable cookies through their browser settings if preferred.",
  },
  {
    title: "7. Customer Rights",
    intro: "Customers have the right to:",
    bullets: [
      "Request access to their personal information",
      "Correct inaccurate information",
      "Request deletion of their data where applicable",
    ],
    footer: "Requests can be made through our support channels.",
  },
  {
    title: "8. Marketing Communication",
    intro: "We may occasionally send:",
    bullets: [
      "Promotions",
      "Product updates",
      "Offers",
      "Announcements",
    ],
    footer: "Customers may unsubscribe from marketing emails at any time.",
  },
  {
    title: "9. Third-Party Links",
    content:
      "Our website may contain links to external websites or services. We are not responsible for the privacy practices of third-party websites.",
  },
  {
    title: "10. Policy Updates",
    content:
      "This Privacy Policy may be updated periodically to reflect operational, legal, or service changes. Continued use of our website after updates indicates acceptance of the revised policy.",
  },
  {
    title: "11. Contact Us",
    intro: "For privacy-related questions or concerns, contact:",
    bullets: [
      "Email: support@vethinteriors.com",
      "WhatsApp Support: Available on our Contact Page",
    ],
  },
];

function SectionBlock({ section }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border border-black/10 dark:border-white/10 rounded-2xl overflow-hidden mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 bg-white dark:bg-black/40 hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-left"
      >
        <h2 className="font-bold text-black/80 dark:text-white/90 text-base sm:text-lg">
          {section.title}
        </h2>
        <span className="text-amber-500 text-xl ml-4 flex-shrink-0">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 pt-2 bg-white dark:bg-black/30 text-black/60 dark:text-white/70 text-sm leading-relaxed">
          {section.content && <p className="mb-3">{section.content}</p>}
          {section.intro && <p className="mb-3">{section.intro}</p>}
          {section.bullets && (
            <ul className="space-y-2">
              {section.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {section.intro2 && <p className="mt-3 mb-2">{section.intro2}</p>}
          {section.footer && <p className="mt-3">{section.footer}</p>}
        </div>
      )}
    </div>
  );
}

const PrivacyPolicy = () => {
  return (
    <div className="bg-white dark:bg-black min-h-screen">
      {/* Hero */}
      <div className="w-full pt-32 pb-10 px-4 bg-black/5 dark:bg-white/5 border-b border-black/10 dark:border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-black/90 dark:text-white mb-3">
            Privacy Policy
          </h1>
          <p className="text-black/50 dark:text-white/50 text-sm">
            At Veth Interiors, we value your privacy and are committed to protecting your personal
            information. This policy explains how we collect, use, and safeguard your data.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        {sections.map((section, i) => (
          <SectionBlock key={i} section={section} />
        ))}

        {/* Contact CTA */}
        <div className="mt-8 bg-black/5 dark:bg-white/5 rounded-2xl p-8 text-center border border-black/10 dark:border-white/10">
          <h3 className="text-lg font-bold text-black/90 dark:text-white mb-2">
            Privacy concerns or data requests?
          </h3>
          <p className="text-black/50 dark:text-white/50 text-sm mb-4">
            Reach out and we'll respond promptly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/2348106079897"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full transition-all"
            >
              WhatsApp Us
            </a>
            <a
              href="mailto:support@vethinteriors.com"
              className="px-8 py-3 border-2 border-amber-500 text-amber-600 dark:text-amber-400 font-bold rounded-full hover:bg-amber-500 hover:text-white transition-all"
            >
              Email Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
